import React, { useState } from 'react';
import styles from './ConfirmAction.module.css';
import Button from '../Button/Button';
import ReactDOM from 'react-dom';

type ConfirmActionProps = {
  isVisible: boolean;
  onClose: () => void;
  clickHandler: () => void | Promise<void>;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  icon?: string;
  severity?: 'warning' | 'danger';
  loading?: boolean;
  confirmDisabled?: boolean;
  children?: React.ReactNode;
};

const ConfirmAction = ({ 
  isVisible, 
  onClose, 
  clickHandler,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  icon,
  severity = 'danger',
  loading = false,
  confirmDisabled = false,
  children
}: ConfirmActionProps) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleConfirm = async () => {
    if (loading || isProcessing || confirmDisabled) return;
    
    setIsProcessing(true);
    try {
      await clickHandler();
      onClose();
    } catch (error) {
      console.error('Confirmation action failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    if (loading || isProcessing) return;
    onClose();
  };

  const isDisabled = loading || isProcessing;
  const confirmBlocked = isDisabled || confirmDisabled;

  if (!isVisible) return null;

  const popupElement = document.getElementById("popup");
  
  if (!popupElement) {
    console.error("Popup element not found!");
    return null;
  }

  return ReactDOM.createPortal(
    <div className={styles.modalOverlay} onClick={handleCancel}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          {icon && (
            <img 
              src={icon} 
              alt="" 
              className={`${styles.icon} ${styles[`icon_${severity}`]}`}
            />
          )}
          {title && <h3 className={styles.title}>{title}</h3>}
        </div>

        <div className={styles.message}>
          <p>{message}</p>
          {children}
        </div>

        <div className={styles.actions}>
          <Button
            clickHandler={handleCancel}
            disabled={isDisabled}
            colour="#6c757d"
            textColour="#fff"
            size="Small"
          >
            {cancelText}
          </Button>
          
          <Button
            clickHandler={handleConfirm}
            disabled={confirmBlocked}
            colour={severity === 'danger' ? '#dc3545' : '#17a2b8'}
            textColour="#fff"
            size="Small"
          >
            {loading || isProcessing ? 'Processing...' : confirmText}
          </Button>
        </div>
      </div>
    </div>,
    popupElement
  );
};

export default ConfirmAction; 